export class PuzzleValidator {
    /**
     * Validate puzzle structure for variable grid sizes
     */
    static validate(puzzle) {
        const errors = [];

        // Check puzzle has categories
        if (!puzzle.categories || !Array.isArray(puzzle.categories)) {
            errors.push('Missing or invalid categories array');
            return { valid: false, errors };
        }

        const categoryCount = puzzle.categories.length;

        // Check category count is reasonable (4-10 for Mon-Sun)
        if (categoryCount < 4 || categoryCount > 10) {
            errors.push(`Invalid category count: ${categoryCount}. Must be between 4 and 10`);
        }

        // Calculate total words across all categories
        const totalWords = puzzle.categories.reduce((sum, cat) => {
            if (cat.words && Array.isArray(cat.words)) {
                return sum + cat.words.length;
            }
            return sum;
        }, 0);

        const gridSize = Math.sqrt(totalWords);

        // Ensure grid is square
        if (!Number.isInteger(gridSize)) {
            errors.push(`Total word count ${totalWords} does not form a square grid (√${totalWords} = ${gridSize})`);
        }

        // Check each category has correct structure
        puzzle.categories.forEach((cat, idx) => {
            // Check category has required fields
            if (!cat.id) {
                errors.push(`Category ${idx} missing id field`);
            }
            if (!cat.answer) {
                errors.push(`Category ${idx} (${cat.id}) missing answer field`);
            }
            if (!cat.words || !Array.isArray(cat.words)) {
                errors.push(`Category ${idx} (${cat.id}) missing or invalid words array`);
                return;
            }

            // For Monday-Thursday (4-7 categories), enforce equal distribution
            // For Friday-Sunday: 7 categories with variable words (8x8, 10x10, 12x12)
            if (categoryCount <= 7 && categoryCount === gridSize) {
                // Traditional square grid where categories = grid size
                const expectedWords = categoryCount;
                if (cat.words.length !== expectedWords) {
                    errors.push(`Category ${idx} (${cat.id}) has ${cat.words.length} words, expected ${expectedWords} for ${categoryCount}x${categoryCount} grid`);
                }
            } else if (categoryCount === 7 && gridSize > 7) {
                // Friday-Sunday: 7 categories with variable words
                // Just verify total adds up (already checked above)
                // Each category must have an even number of words for pairing
                if (cat.words.length % 2 !== 0) {
                    errors.push(`Category ${idx} (${cat.id}) has ${cat.words.length} words, must be even for pairing`);
                }
            }

            // Check each word has required fields
            cat.words.forEach((word, wordIdx) => {
                if (!word.text) {
                    errors.push(`Category ${idx} (${cat.id}) word ${wordIdx} missing text`);
                }
                if (word.pairId === undefined) {
                    errors.push(`Category ${idx} (${cat.id}) word ${wordIdx} (${word.text}) missing pairId`);
                }
            });

            // Check deep dive structure
            if (!cat.deepDive) {
                errors.push(`Category ${idx} (${cat.id}) missing deepDive`);
                return;
            }

            // Support both v1.0 (pairs) and v2.0 (mechanic/items) formats
            if (cat.deepDive.pairs) {
                // v1.0 format validation
                if (!Array.isArray(cat.deepDive.pairs)) {
                    errors.push(`Category ${idx} (${cat.id}) deepDive pairs is not an array`);
                    return;
                }

                // Deep dive must always have exactly 4 pairs (8 words total)
                if (cat.deepDive.pairs.length !== 4) {
                    errors.push(`Category ${idx} (${cat.id}) deep dive has ${cat.deepDive.pairs.length} pairs, must have exactly 4`);
                }

                // Check each deep dive pair
                cat.deepDive.pairs.forEach((pair, pairIdx) => {
                    if (!pair.words || !Array.isArray(pair.words) || pair.words.length !== 2) {
                        errors.push(`Category ${idx} (${cat.id}) deep dive pair ${pairIdx} must have exactly 2 words`);
                    }
                });
            } else if (cat.deepDive.mechanic) {
                // v2.0 format validation
                if (!cat.deepDive.instruction) {
                    errors.push(`Category ${idx} (${cat.id}) deepDive missing instruction`);
                }
                if (!cat.deepDive.items || !Array.isArray(cat.deepDive.items)) {
                    errors.push(`Category ${idx} (${cat.id}) deepDive missing or invalid items array`);
                    return;
                }
                if (cat.deepDive.items.length < 2) {
                    errors.push(`Category ${idx} (${cat.id}) deepDive has ${cat.deepDive.items.length} items, must have at least 2`);
                }
            } else {
                errors.push(`Category ${idx} (${cat.id}) deepDive has neither pairs (v1.0) nor mechanic (v2.0) format`);
            }
        });

        return { valid: errors.length === 0, errors };
    }
}
